import { Component, inject, signal } from '@angular/core';
import JSZip from 'jszip';
import { Muxer, ArrayBufferTarget } from 'mp4-muxer';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { assert } from 'src/app/utils/assert.util';
import * as THREE from 'three';
import { EXRLoader, RGBELoader } from 'three-stdlib';

@Component({
  selector: 'app-admin-controls-single-texture-viewer',
  templateUrl: './admin-controls-single-texture-viewer.component.html',
  styleUrl: './admin-controls-single-texture-viewer.component.scss'
})
export class AdminControlsSingleTextureViewerComponent {
  private loadingService = inject(LoadingService);
  textureMap = signal(new Map<string, File>());
  fileNamePrefix = '';

  // Turntable animation settings — tune here. More frames / higher fps means a
  // smoother clip; larger size and bitrate mean sharper output but bigger files.
  turntableFrameCount = 144;   // unique rendered frames over one full rotation
  turntableSize = 512;         // px, square; keep even for H.264
  turntableFps = 15;           // 144 frames / 15 fps ≈ 9.6s per rotation
  turntableBitrate = 12_000_000;

  async saveTurntable() {
    if (typeof VideoEncoder === 'undefined') {
      throw new Error('This browser does not support in-page MP4 encoding (WebCodecs). Use a recent Chrome or Edge.');
    }
    this.loadingService.load('AdminControls::turntable');
    try {
      const size = this.turntableSize;
      const fps = this.turntableFps;
      const frames: string[] = await (window as any).mainImageComponent.getTurntableImages(this.turntableFrameCount, size);

      const muxer = new Muxer({
        target: new ArrayBufferTarget(),
        video: { codec: 'avc', width: size, height: size },
        fastStart: 'in-memory'
      });
      const encoder = new VideoEncoder({
        output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
        error: (error) => console.error('Turntable MP4 encode error', error)
      });
      encoder.configure({ codec: 'avc1.42001f', width: size, height: size, bitrate: this.turntableBitrate, framerate: fps });

      const frameDurationMicros = 1_000_000 / fps;
      for (let i = 0; i < frames.length; i++) {
        const canvas = await this.decodeToCanvas(frames[i]);
        const frame = new VideoFrame(canvas, { timestamp: Math.round(i * frameDurationMicros), duration: Math.round(frameDurationMicros) });
        encoder.encode(frame, { keyFrame: i % fps === 0 });
        frame.close();
      }
      await encoder.flush();
      muxer.finalize();

      const blob = new Blob([muxer.target.buffer], { type: 'video/mp4' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${this.fileNamePrefix}_turntable.mp4`;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally {
      this.loadingService.done('AdminControls::turntable');
    }
  }

  private decodeToCanvas(dataUrl: string): Promise<HTMLCanvasElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const context = canvas.getContext('2d');
        if (!context) {
          reject(new Error('Could not get 2d context for turntable frame'));
          return;
        }
        context.drawImage(img, 0, 0);
        resolve(canvas);
      };
      img.onerror = () => reject(new Error('Could not decode turntable frame'));
      img.src = dataUrl;
    });
  }

  async saveImages() {
    const imageNumbers = [1, 2, 3, 6, 7, 8, 9];
    const images: string[] = await (window as any).mainImageComponent.getPresetProductImages();
    const zip = new JSZip();
    images.forEach((dataUrl, index) => {
      const base64 = dataUrl.split(',')[1];
      zip.file(`${this.fileNamePrefix}_P${imageNumbers[index]}.jpg`, base64, { base64: true });
    });
    const blob = await zip.generateAsync({ type: 'blob' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${this.fileNamePrefix}.zip`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  textureInputs = [
    {
      title: 'Design Textures',
      items: [
        {
          title: 'Base Color',
          name: 'texture.design.map'
        },
        {
          title: 'Ambient Occlusion',
          name: 'texture.design.aoMap'
        },
        {
          title: 'Metalness/Roughness',
          name: 'texture.design.metalnessMap|texture.design.roughnessMap'
        },
        {
          title: 'Normal',
          name: 'texture.design.normalMap'
        },
        {
          title: 'Displacement',
          name: 'texture.design.displacementMap'
        },
      ]
    },
    {
      title: 'Edge Textures',
      items: [
        {
          title: 'Base Color',
          name: 'texture.edge.map'
        },
        {
          title: 'Ambient Occlusion',
          name: 'texture.edge.aoMap'
        },
        {
          title: 'Metalness/Roughness',
          name: 'texture.edge.metalnessMap|texture.edge.roughnessMap'
        },
        {
          title: 'Normal',
          name: 'texture.edge.normalMap'
        },
        {
          title: 'Displacement',
          name: 'texture.edge.displacementMap'
        },
      ]
    },
    {
      title: 'Inside Textures',
      items: [
        {
          title: 'Base Color',
          name: 'texture.inside.map'
        },
        {
          title: 'Ambient Occlusion',
          name: 'texture.inside.aoMap'
        },
        {
          title: 'Metalness/Roughness',
          name: 'texture.inside.metalnessMap|texture.inside.roughnessMap'
        },
        {
          title: 'Normal',
          name: 'texture.inside.normalMap'
        },
        {
          title: 'Displacement',
          name: 'texture.inside.displacementMap'
        },
      ]
    }
  ];

  async fileChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files?.[0]) {

      this.loadingService.load('AdminControls::filechange');
        const file = target.files[0];

        assert(file, 'Key does not exist in filemap');
        const fileURL = URL.createObjectURL(file);

        const targetNames = target.name.split('|');
        for (let i = 0; i < targetNames.length; i++) {
          const [type, ...attributes] = targetNames[i].split('.');
          if (type !== 'texture') {
            attributes.unshift('');
          }
          const [location, attribute] = attributes;

          if (type === 'texture') {
            this.textureMap.update(m => {
              m.set(attributes.join('.'), file);
              return m;
            });
            const textureLoader = new THREE.TextureLoader();
            await this.modifyModelMeshesMaterial(location, async (material) => {
              if (material) {
                material[attribute] = await textureLoader.loadAsync(fileURL);
                material[attribute].anisotropy = (window as any).mainImageComponent.renderer?.capabilities.getMaxAnisotropy();
              }
            });
          } else if (type === 'scene') {
            const environmentLoader = file.name.includes('.exr') ? new EXRLoader() : new RGBELoader();
            await new Promise<void>(res => environmentLoader.load(fileURL, (texture) => {
              const scene = (window as any).mainImageComponent.scene;
              assert(scene, 'Scene was null');
              texture.mapping = THREE.EquirectangularReflectionMapping;
              texture.needsUpdate = true;
              scene[attribute] = texture;
              scene.environmentRotation.z = -100 * Math.PI / 180;
              scene.environmentRotation.y = -35 * Math.PI / 180;
              scene.environmentRotation.x = 90 * Math.PI / 180;
              texture.dispose();
              res();
            }));
          } else if (type === 'model') {
            await (window as any).mainImageComponent.loadModelForAdminControls(fileURL);
            await this.applyTextures();
          }
        }

      this.loadingService.done('AdminControls::filechange');
    }
  }

  async applyTextures() {
    const keys = Array.from(this.textureMap().keys());
    await Promise.all(keys.map(async key => {
      const textureLoader = new THREE.TextureLoader();
      const [location, attribute] = key.split('.');
      await this.modifyModelMeshesMaterial(location, async (material) => {
        if (material) {
          const fileURL = URL.createObjectURL(this.textureMap().get(key)!);
          material[attribute] = await textureLoader.loadAsync(fileURL);
          material[attribute].anisotropy = (window as any).mainImageComponent.renderer?.capabilities.getMaxAnisotropy();
        }
      });
    }));
  }

  async modifyModelMeshesMaterial(location: string, callback: (material: any) => void) {
    const modelMeshes: any[] = [];
    (window as any).mainImageComponent.model.scene.traverse((descendant: any) => {
      if (descendant.isMesh) {
        modelMeshes.push(descendant);
      }
    });

    const locationMap: Record<string, number | null> = {
      'design': 0,
      'edge': modelMeshes.length === 2 ? NaN : 1,
      'inside': modelMeshes.length === 2 ? 1 : 2
    }

    const index = locationMap[location] ?? NaN;
    if (!isNaN(index)) {
      await callback(modelMeshes[index].material);
      modelMeshes[index].material.needsUpdate = true
    } else {
      await callback(null);
    }
  }

  clearButtonClick(textureKey: string) {
    textureKey.split('|').forEach(key => {
      const [location, attribute] = key.split('.');
      this.modifyModelMeshesMaterial(location, (material) => {
        material[attribute] = null;
      });
      if (this.textureMap().has(key)) {
        this.textureMap.update(map => {
          map.delete(key);
          return map;
        });
      }
    });
  }

  clearInput(event: Event) {
    (event.target as HTMLInputElement).value = '';
  }
}
